import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/auth/authStore'
import { api, uploadForm } from './client'
import type {
  AuditLogEntry,
  GstinDetails,
  DepartmentView,
  CateringPackage,
  Company,
  CompanyModule,
  PackageItem,
  ConsolidatedItem,
  CustomerDetail,
  CustomerOutstanding,
  CustomerSummary,
  DeliverySplit,
  EInvoice as EInvoiceStatus,
  KitchenSlip,
  EInvoiceSettings,
  IntegrationAccount,
  IntegrationTestResult,
  EnquiryDetail,
  EnquirySummary,
  ExecutiveDashboard,
  InboxSummary,
  FeedbackSummary,
  FeedbackView,
  FeedbackAttachment,
  FeedbackContact,
  FeedbackLinkSent,
  FinanceDashboard,
  Followup,
  Hall,
  HallAvailability,
  SlotDay,
  Invoice,
  KitchenDashboard,
  KitchenRequest,
  ManpowerRequirement,
  EbReading,
  EquipmentItem,
  GatePass,
  GatePassTemplate,
  DishRequirements,
  MenuCategory,
  MenuImportResult,
  MenuItem,
  MuhurthamDate,
  MuhurthamImportResult,
  TamilDate,
  NotificationView,
  OperationalSummary,
  OperationsDashboard,
  MovedCustomer,
  OrderDetail,
  OrderStatus,
  OrderSummary,
  Outlet,
  PageResponse,
  PackageVersion,
  EventTypeView,
  SessionTypeView,
  OrderLimitView,
  ServiceTypeView,
  PreBillingCheck,
  OrderDocumentAudit,
  CreditNote,
  Payment,
  Refund,
  Refundable,
  WriteOffSuggestion,
  PaymentSchedule,
  AdvancePolicyView,
  CancellationPolicyView,
  NumberingSettings,
  NumberSeriesView,
  AppUser,
  PendingApproval,
  PermissionView,
  PolicySettings,
  RoleView,
  ServiceTermsView,
  MenuSheet,
  PriceCalculation,
  QuotationDetail,
  QuotationPreview,
  QuotationSummary,
  RecoveryCase,
  ReferenceItem,
  ScheduledReportView,
  SelectionItem,
  StaffMember,
  Trip,
  Vehicle,
} from './types'

/** Query keys in one place so invalidation stays consistent across screens. */
export const keys = {
  outlets: ['outlets'] as const,
  halls: (outletId?: string) => ['halls', outletId ?? 'all'] as const,
  availability: (from: string, to: string, outletId?: string) =>
    ['availability', from, to, outletId ?? 'all'] as const,
  customers: (filters: unknown) => ['customers', filters] as const,
  customer: (id: string) => ['customer', id] as const,
  enquiries: (filters: unknown) => ['enquiries', filters] as const,
  enquiry: (id: string) => ['enquiry', id] as const,
  followupsDue: ['followups', 'due'] as const,
  menuCategories: ['menu', 'categories'] as const,
  menuItems: (filters: unknown) => ['menu', 'items', filters] as const,
  packages: (filters: unknown = {}) => ['packages', filters] as const,
  packageVersion: (id: string) => ['packageVersion', id] as const,
  packageSlip: (serviceType: string) => ['packageSlip', serviceType] as const,
  quotations: (filters: unknown) => ['quotations', filters] as const,
  quotation: (id: string) => ['quotation', id] as const,
  orders: (filters: unknown) => ['orders', filters] as const,
  order: (id: string) => ['order', id] as const,
  operationalSummary: (id: string) => ['order', id, 'operational-summary'] as const,
  kitchenBoard: (filters: unknown) => ['kitchen', 'board', filters] as const,
  kitchenConsolidated: (filters: unknown) => ['kitchen', 'consolidated', filters] as const,
  manpower: (orderId: string) => ['manpower', orderId] as const,
  staff: (filters: unknown) => ['staff', filters] as const,
  vehicles: (filters: unknown) => ['vehicles', filters] as const,
  trips: (filters: unknown) => ['trips', filters] as const,
  invoices: (filters: unknown) => ['invoices', filters] as const,
  payments: (filters: unknown) => ['payments', filters] as const,
  paymentSchedule: (orderId: string) => ['payment-schedule', orderId] as const,
  outstanding: ['reports', 'outstanding'] as const,
  feedback: (filters: unknown) => ['feedback', filters] as const,
  feedbackSummary: (outletId?: string) => ['feedback', 'summary', outletId ?? 'all'] as const,
  recoveryCases: (status?: string) => ['recovery-cases', status ?? 'all'] as const,
  dashboards: (name: string, filters?: unknown) => ['dashboard', name, filters] as const,
  approvals: ['approvals', 'pending'] as const,
  notifications: ['notifications'] as const,
}

// ------------------------------------------------------------- organization

export const useOutlets = () =>
  useQuery({ queryKey: keys.outlets, queryFn: () => api.get<Outlet[]>('/api/v1/outlets') })

/**
 * The branch a query is answered for.
 *
 * <p>What the caller asked for, if it asked: a hall belongs to the order's own
 * branch, not to whichever one the toolbar happens to be set to. Otherwise the
 * branch being worked in, so every list, board and total narrows to it at once
 * rather than each screen carrying its own filter and disagreeing.</p>
 *
 * <p>Undefined means the whole business, which is what the server does with an
 * outlet it was not given.</p>
 */
const useScopedOutlet = (explicit?: string) =>
  // Blank is not a choice of branch: a picker set to "all" reads as an empty
  // string, and taking that literally would override the toolbar with nothing
  // and quietly widen the screen back to the whole business.
  useAuthStore((state) => (explicit || undefined) ?? state.activeOutletId ?? undefined)

const companiesKey = ['companies'] as const

/**
 * The companies the caller may see: their own, or every one of them if they
 * administer the platform.
 */
export const useCompanies = () =>
  useQuery({ queryKey: companiesKey, queryFn: () => api.get<Company[]>('/api/v1/companies') })

/**
 * Every outlet of one company, for mapping people to them.
 *
 * <p>Different from {@link useOutlets}, which answers "the outlets you may work
 * in". This answers "the outlets that exist there" - switched-off ones
 * included, and for a company other than your own when you administer the
 * platform.</p>
 */
export const useCompanyOutlets = (companyId?: string) =>
  useQuery({
    queryKey: ['company-outlets', companyId ?? ''] as const,
    queryFn: () => api.get<Outlet[]>(`/api/v1/companies/${companyId}/outlets`),
    enabled: Boolean(companyId),
  })

/**
 * The outlets of every business a person may work in, each carrying the name of
 * the company it belongs to.
 *
 * <p>Somebody mapped to two businesses has to be mappable to outlets in both.
 * Reading only their home company's outlets left the second business's branches
 * unreachable, so an account could work in a company it could see no outlet
 * of.</p>
 */
export const useOutletsOfCompanies = (companyIds: string[]) => {
  const results = useQueries({
    queries: companyIds.map((companyId) => ({
      queryKey: ['company-outlets', companyId] as const,
      queryFn: () => api.get<Outlet[]>(`/api/v1/companies/${companyId}/outlets`),
      enabled: Boolean(companyId),
    })),
  })
  return {
    outlets: results.flatMap((result) => result.data ?? []),
    isLoading: results.some((result) => result.isLoading),
  }
}

/**
 * What every business a person may work in sells, for granting them a line of
 * it.
 *
 * <p>Read per company rather than for whoever is doing the editing, because a
 * service belongs to a business: two of a group can carry the same code and
 * disagree about it entirely - one sells over a counter and has withdrawn
 * banquets, the other the reverse. Asked once for the caller's company, the
 * list offered the wrong business's lines.</p>
 *
 * <p>Withdrawn ones come back too, and are dropped at the point of use. A line
 * somebody was granted before it was withdrawn has to stay readable as a name,
 * or it shows as a bare code nobody can act on.</p>
 */
export const useServiceTypesOfCompanies = (companyIds: string[]) => {
  const results = useQueries({
    queries: companyIds.map((companyId) => ({
      queryKey: ['service-types', companyId, '', true] as const,
      queryFn: () =>
        api.get<ServiceTypeView[]>('/api/v1/service-types', {
          companyId,
          includeInactive: true,
        }),
      enabled: Boolean(companyId),
    })),
  })
  return {
    services: results.flatMap((result) => result.data ?? []),
    isLoading: results.some((result) => result.isLoading),
  }
}

const modulesKey = (companyId?: string) => ['company-modules', companyId ?? 'own'] as const

/** The parts of the application a company runs. Its own when none is named. */
export const useCompanyModules = (companyId?: string) =>
  useQuery({
    queryKey: modulesKey(companyId),
    queryFn: () => api.get<CompanyModule[]>('/api/v1/companies/modules', { companyId }),
  })

export const useSaveCompanyModules = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ companyId, enabled }: { companyId?: string; enabled: string[] }) =>
      api.put<CompanyModule[]>(
        `/api/v1/companies/modules${companyId ? `?companyId=${companyId}` : ''}`,
        enabled,
      ),
    // A module carries its permissions, so what the caller may reach has just
    // changed. Everything is refetched rather than guessing what is affected.
    onSuccess: () => queryClient.invalidateQueries(),
  })
}

export const useDeleteCompany = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/companies/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companiesKey })
      queryClient.invalidateQueries({ queryKey: keys.outlets })
    },
  })
}

/**
 * Puts a company's logo in the document store and hands back its id.
 *
 * <p>Stored against the company as its owner, so it is found again from the
 * company rather than only from the id the form happens to be holding.</p>
 */
/**
 * The mark printed at the head of this branch's documents.
 *
 * <p>Uploaded against the outlet, not the company: what heads a bill for food
 * is the premises it came out of, and a group trading two brands from two
 * premises has two marks.</p>
 */
export const useUploadOutletLogo = () =>
  useMutation({
    mutationFn: async ({ outletId, file }: { outletId: string; file: File }) => {
      const body = new FormData()
      body.append('file', file)
      body.append('ownerType', 'OUTLET')
      body.append('ownerId', outletId)
      body.append('documentType', 'LOGO')
      return uploadForm<{ id: string }>('/api/v1/documents', body)
    },
  })

export const useSaveCompany = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: unknown }) =>
      id
        ? api.put<Company>(`/api/v1/companies/${id}`, body)
        : api.post<Company>('/api/v1/companies', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: companiesKey })
      // A new company arrives with its first outlet.
      queryClient.invalidateQueries({ queryKey: keys.outlets })
    },
  })
}

/**
 * Bookable spaces. `spaceType` narrows to banquet halls or dining areas; both
 * come back when it is left off, which is what the availability board wants.
 */
/**
 * The lines of business on offer, narrowed to one branch when asked.
 *
 * <p>A screen that lets someone sell a banquet at a takeaway counter produces
 * an order nobody can fulfil, so the branch decides what is offered.</p>
 */
/** Every configured value for the company being looked at. */
export const useConfiguration = () =>
  useQuery({
    queryKey: ['configuration'] as const,
    queryFn: () => api.get<{ key: string; value: string; outletId?: string }[]>(
      '/api/v1/configuration',
    ),
  })

/** Sets one configured value. The change is audited. */
export const useSaveConfiguration = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { key: string; value: string; valueType?: string; description?: string }) =>
      api.put('/api/v1/configuration', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['configuration'] }),
  })
}

export const useServiceTypes = (
  options: { companyId?: string; outletId?: string; includeInactive?: boolean } = {},
) =>
  useQuery({
    queryKey: [
      'service-types',
      options.companyId ?? '',
      options.outletId ?? '',
      options.includeInactive ?? false,
    ] as const,
    queryFn: () =>
      api.get<ServiceTypeView[]>('/api/v1/service-types', {
        companyId: options.companyId,
        outletId: options.outletId,
        includeInactive: options.includeInactive,
      }),
  })

const useServiceTypeMutation = <TVariables,>(fn: (variables: TVariables) => Promise<unknown>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['service-types'] }),
  })
}

export const useSaveServiceType = () =>
  useServiceTypeMutation(
    ({ id, companyId, ...body }: { id?: string; companyId?: string } & Record<string, unknown>) => {
      const query = companyId ? `?companyId=${companyId}` : ''
      return id
        ? api.put<ServiceTypeView>(`/api/v1/service-types/${id}${query}`, body)
        : api.post<ServiceTypeView>(`/api/v1/service-types${query}`, body)
    },
  )

export const useDeactivateServiceType = () =>
  useServiceTypeMutation(({ id, companyId }: { id: string; companyId?: string }) =>
    api.delete(`/api/v1/service-types/${id}${companyId ? `?companyId=${companyId}` : ''}`),
  )

/**
 * The occasions a line of business is sold for.
 *
 * <p>Without a service, every occasion the company keeps, which is the master
 * screen. With one, what that service offers - and an empty answer is the
 * answer: a service with none mapped is never asked what occasion it is
 * for.</p>
 */
/** The parts of the business a giveaway can be charged to. */
export const useDepartments = (
  options: { companyId?: string; includeInactive?: boolean } = {},
) =>
  useQuery({
    queryKey: ['departments', options.companyId ?? '', options.includeInactive ?? false] as const,
    queryFn: () =>
      api.get<DepartmentView[]>('/api/v1/departments', {
        companyId: options.companyId,
        includeInactive: options.includeInactive,
      }),
  })

const useDepartmentMutation = <TVariables,>(fn: (variables: TVariables) => Promise<unknown>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['departments'] }),
  })
}

export const useSaveDepartment = () =>
  useDepartmentMutation(
    ({ id, companyId, ...body }: { id?: string; companyId?: string } & Record<string, unknown>) => {
      const query = companyId ? `?companyId=${companyId}` : ''
      return id
        ? api.put<DepartmentView>(`/api/v1/departments/${id}${query}`, body)
        : api.post<DepartmentView>(`/api/v1/departments${query}`, body)
    },
  )

export const useRemoveDepartment = () =>
  useDepartmentMutation(({ id, companyId }: { id: string; companyId?: string }) =>
    api.delete<void>(`/api/v1/departments/${id}${companyId ? `?companyId=${companyId}` : ''}`),
  )

export const useEventTypes = (
  options: { companyId?: string; serviceType?: string; includeInactive?: boolean } = {},
) =>
  useQuery({
    queryKey: [
      'event-types',
      options.companyId ?? '',
      options.serviceType ?? '',
      options.includeInactive ?? false,
    ] as const,
    queryFn: () =>
      api.get<EventTypeView[]>('/api/v1/event-types', {
        companyId: options.companyId,
        serviceType: options.serviceType,
        includeInactive: options.includeInactive,
      }),
  })

const useEventTypeMutation = <TVariables,>(fn: (variables: TVariables) => Promise<unknown>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['event-types'] }),
  })
}

export const useSaveEventType = () =>
  useEventTypeMutation(
    ({ id, companyId, ...body }: { id?: string; companyId?: string } & Record<string, unknown>) => {
      const query = companyId ? `?companyId=${companyId}` : ''
      return id
        ? api.put<EventTypeView>(`/api/v1/event-types/${id}${query}`, body)
        : api.post<EventTypeView>(`/api/v1/event-types${query}`, body)
    },
  )

export const useRemoveEventType = () =>
  useEventTypeMutation(({ id, companyId }: { id: string; companyId?: string }) =>
    api.delete(`/api/v1/event-types/${id}${companyId ? `?companyId=${companyId}` : ''}`),
  )

/**
 * The sittings the company sells by, and what each is offered for.
 *
 * <p>With withdrawn ones included this is the master screen. With a service,
 * only what that service sells - and an empty answer is the answer: a service
 * with none assigned is not asked which sitting it is.</p>
 */
export const useSessionTypes = (
  options: { companyId?: string; serviceType?: string; includeInactive?: boolean } = {},
) =>
  useQuery({
    queryKey: [
      'session-types',
      options.companyId ?? '',
      options.serviceType ?? '',
      options.includeInactive ?? false,
    ] as const,
    queryFn: () =>
      api.get<SessionTypeView[]>('/api/v1/session-types', {
        companyId: options.companyId,
        serviceType: options.serviceType,
        includeInactive: options.includeInactive,
      }),
  })

const useSessionTypeMutation = <TVariables,>(fn: (variables: TVariables) => Promise<unknown>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['session-types'] }),
  })
}

export const useSaveSessionType = () =>
  useSessionTypeMutation(
    ({ id, companyId, ...body }: { id?: string; companyId?: string } & Record<string, unknown>) => {
      const query = companyId ? `?companyId=${companyId}` : ''
      return id
        ? api.put<SessionTypeView>(`/api/v1/session-types/${id}${query}`, body)
        : api.post<SessionTypeView>(`/api/v1/session-types${query}`, body)
    },
  )

export const useRemoveSessionType = () =>
  useSessionTypeMutation(({ id, companyId }: { id: string; companyId?: string }) =>
    api.delete(`/api/v1/session-types/${id}${companyId ? `?companyId=${companyId}` : ''}`),
  )

/** What each sitting of each line of business can take in a day. */
export const useOrderLimits = (companyId?: string) =>
  useQuery({
    queryKey: ['order-limits', companyId ?? ''] as const,
    queryFn: () => api.get<OrderLimitView[]>('/api/v1/order-limits', { companyId }),
  })

const useOrderLimitMutation = <TVariables,>(fn: (variables: TVariables) => Promise<unknown>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['order-limits'] }),
  })
}

export const useSaveOrderLimit = () =>
  useOrderLimitMutation(
    ({ id, companyId, ...body }: { id?: string; companyId?: string } & Record<string, unknown>) => {
      const query = companyId ? `?companyId=${companyId}` : ''
      return id
        ? api.put<OrderLimitView>(`/api/v1/order-limits/${id}${query}`, body)
        : api.post<OrderLimitView>(`/api/v1/order-limits${query}`, body)
    },
  )

export const useRemoveOrderLimit = () =>
  useOrderLimitMutation(({ id, companyId }: { id: string; companyId?: string }) =>
    api.delete(`/api/v1/order-limits/${id}${companyId ? `?companyId=${companyId}` : ''}`),
  )

export const useHalls = (outletId?: string, spaceType?: 'HALL' | 'DINING') =>
  useQuery({
    queryKey: [...keys.halls(outletId), spaceType ?? 'ALL'] as const,
    queryFn: () => api.get<Hall[]>('/api/v1/halls', { outletId, spaceType }),
  })

/** One space, always read fresh, so an editor open on it sees its own edits. */
export const useHall = (id?: string) =>
  useQuery({
    queryKey: ['hall', id ?? ''] as const,
    queryFn: () => api.get<Hall>(`/api/v1/halls/${id}`),
    enabled: Boolean(id),
  })

const orgMutation = <TVariables,>(fn: (variables: TVariables) => Promise<unknown>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outlets'] })
      // A different cache from the one above: outlets scoped to one company,
      // read by the screens that map a service or a person to a branch. An
      // outlet opened or corrected there went on showing the list as it stood
      // before, because nothing ever told this cache it was stale.
      queryClient.invalidateQueries({ queryKey: ['company-outlets'] })
      queryClient.invalidateQueries({ queryKey: ['halls'] })
      queryClient.invalidateQueries({ queryKey: ['hall'] })
    },
  })
}

/**
 * Opens an outlet, or corrects one.
 *
 * <p>A company may be named when opening one, which is how a platform
 * administrator sets up a second business without signing into it. It is not
 * accepted on a correction: an outlet carries its halls, orders and staff, and
 * moving it to another business would leave all of that filed under a company
 * that never sold it.</p>
 */
export const useSaveOutlet = () =>
  orgMutation(({ id, companyId, body }: { id?: string; companyId?: string; body: unknown }) =>
    id
      ? api.put<Outlet>(`/api/v1/outlets/${id}`, body)
      : api.post<Outlet>(
          `/api/v1/outlets${companyId ? `?companyId=${companyId}` : ''}`,
          body,
        ),
  )

/** Adds or replaces one slot on a space. Slots are set up by hand, per space. */
export const useSaveHallSlot = () =>
  orgMutation(({ hallId, body }: { hallId: string; body: unknown }) =>
    api.post<Hall>(`/api/v1/halls/${hallId}/time-slots`, body),
  )

/**
 * The heads a job may be taken for at this branch.
 *
 * <p>Asked so the screen can say it before a number is typed rather than
 * refusing after it has been.</p>
 */
export const usePaxLimits = (serviceType?: string, outletId?: string) =>
  useQuery({
    queryKey: ['pax-limits', serviceType ?? '', outletId ?? ''] as const,
    queryFn: () =>
      api.get<{ minPax: number | null; maxPax: number | null }>(
        '/api/v1/service-types/pax-limits',
        { serviceType, outletId },
      ),
    enabled: Boolean(serviceType),
  })

/** What each branch will take a service for, where it differs from the business. */
export const useBranchLimits = (serviceTypeId?: string) =>
  useQuery({
    queryKey: ['branch-limits', serviceTypeId ?? ''] as const,
    queryFn: () =>
      api.get<{ outletId: string; minPax: number | null; maxPax: number | null }[]>(
        `/api/v1/service-types/${serviceTypeId}/branch-limits`,
      ),
    enabled: Boolean(serviceTypeId),
  })

/** Both blank withdraws the row, and the branch follows the business again. */
export const useSaveBranchLimit = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ serviceTypeId, outletId, minPax, maxPax }: {
      serviceTypeId: string
      outletId: string
      minPax?: number | null
      maxPax?: number | null
    }) =>
      api.put<void>(`/api/v1/service-types/${serviceTypeId}/branch-limits`, {
        outletId,
        minPax: minPax ?? null,
        maxPax: maxPax ?? null,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['branch-limits', variables.serviceTypeId] })
    },
  })
}

/**
 * Adds or corrects one electricity meter on a hall.
 *
 * <p>A hall is rarely one meter: the hall board, the kitchen behind it,
 * sometimes the lift and the pumps.</p>
 */
export const useSaveHallMeter = () =>
  orgMutation(({ hallId, meterId, body }: { hallId: string; meterId?: string; body: unknown }) =>
    meterId
      ? api.put<unknown>(`/api/v1/halls/${hallId}/meters/${meterId}`, body)
      : api.post<unknown>(`/api/v1/halls/${hallId}/meters`, body),
  )

/** Takes a meter out of service. Every reading taken on it stays on the record. */
export const useRemoveHallMeter = () =>
  orgMutation(({ hallId, meterId }: { hallId: string; meterId: string }) =>
    api.delete<void>(`/api/v1/halls/${hallId}/meters/${meterId}`),
  )

/** Withdraws a slot. It stops being sold; anything already sold on it stands. */
export const useRemoveHallSlot = () =>
  orgMutation(({ hallId, slotId }: { hallId: string; slotId: string }) =>
    api.delete<Hall>(`/api/v1/halls/${hallId}/time-slots/${slotId}`),
  )

/** Saves a banquet hall or a dining area; they differ only by `spaceType`. */
/** Withdraws a space; anything already booked in it still reads. */
export const useRemoveHall = () =>
  orgMutation((id: string) => api.delete<void>(`/api/v1/halls/${id}`))

export const useRemoveOutlet = () =>
  orgMutation((id: string) => api.delete<void>(`/api/v1/outlets/${id}`))

export const useSaveHall = () =>
  orgMutation(({ id, body }: { id?: string; body: unknown }) =>
    id ? api.put<Hall>(`/api/v1/halls/${id}`, body) : api.post<Hall>('/api/v1/halls', body),
  )

// ------------------------------------------------------------- availability

export const useHallAvailability = (from: string, to: string, outletId?: string) =>
  useQuery({
    queryKey: keys.availability(from, to, outletId),
    queryFn: () => api.get<HallAvailability[]>('/api/v1/availability/halls', { from, to, outletId }),
  })

/**
 * Which slots of a hall are free over a window. Asked for one date at a time by
 * the quotation builder, so a sitting can be given a slot that is actually open.
 */
/**
 * `excludeOrderId` is the order already being edited, so the board does not
 * call its own slot taken and ask for an override to keep what it already
 * holds.
 */
export const useHallSlotBoard = (
  hallId: string | undefined,
  from: string,
  to: string,
  excludeOrderId?: string,
) =>
  useQuery({
    queryKey: ['hall-slots', hallId ?? '', from, to, excludeOrderId ?? ''] as const,
    queryFn: () =>
      api.get<SlotDay[]>(`/api/v1/availability/halls/${hallId}/slots`, { from, to, excludeOrderId }),
    enabled: Boolean(hallId && from),
  })

export const useCreateHallBlock = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      hallId: string
      blockType: string
      blockStart: string
      blockEnd: string
      reason?: string
    }) => api.post('/api/v1/hall-blocks', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['availability'] }),
  })
}

// ---------------------------------------------------------------- customers

export const useCustomers = (
  filters: {
    q?: string
    type?: string
    outletId?: string
    /** Left out, finds everyone but the deleted. True hides a disabled duplicate. */
    enabled?: boolean
    page?: number
    size?: number
  },
) => {
  const scoped = { ...filters, outletId: useScopedOutlet(filters.outletId) }
  return useQuery({
    queryKey: keys.customers(scoped),
    queryFn: () => api.get<PageResponse<CustomerSummary>>('/api/v1/customers', scoped),
  })
}

/** Whether a customer can sign in to the portal, and how that stands. */
export const usePortalAccess = (customerId?: string) =>
  useQuery({
    queryKey: ['portal-access', customerId ?? ''] as const,
    queryFn: () =>
      api.get<{
        granted: boolean
        active: boolean
        locked: boolean
        username?: string
        lastLoginAt?: string
      }>(`/api/v1/customers/${customerId}/portal-access`),
    enabled: Boolean(customerId),
  })

export const useGrantPortalAccess = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ customerId, password }: { customerId: string; password: string }) =>
      api.post<{ username: string; password: string }>(
        `/api/v1/customers/${customerId}/portal-access`, { password }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal-access'] }),
  })
}

export const useRevokePortalAccess = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (customerId: string) =>
      api.delete<void>(`/api/v1/customers/${customerId}/portal-access`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['portal-access'] }),
  })
}

export const useCustomer = (id?: string) =>
  useQuery({
    queryKey: keys.customer(id ?? ''),
    queryFn: () => api.get<CustomerDetail>(`/api/v1/customers/${id}`),
    enabled: Boolean(id),
  })

export const useSaveCustomer = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id
        ? api.put<CustomerDetail>(`/api/v1/customers/${id}`, body)
        : api.post<CustomerDetail>('/api/v1/customers', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers'] }),
  })
}

/**
 * Keeps a duplicate off the pickers a new quotation or order is started
 * from, without deleting it - its order history stays exactly where it is.
 */
export const useSetCustomerEnabled = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.post<CustomerDetail>(`/api/v1/customers/${id}/enabled`, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers'] }),
  })
}

/** Looks a GSTIN up against the GST network, for filling a customer form in. */
export const useGstinLookup = () =>
  useMutation({
    mutationFn: (gstin: string) => api.get<GstinDetails>('/api/v1/customers/gstin-lookup', { gstin }),
  })

// ---------------------------------------------------------------- enquiries

export const useEnquiries = (filters: Record<string, unknown>) => {
  const scoped = { ...filters, outletId: useScopedOutlet(filters.outletId as string | undefined) }
  return useQuery({
    queryKey: keys.enquiries(scoped),
    queryFn: () =>
      api.get<PageResponse<EnquirySummary>>(
        '/api/v1/enquiries',
        scoped as Record<string, string | number | undefined>,
      ),
  })
}

export const useEnquiry = (id?: string) =>
  useQuery({
    queryKey: keys.enquiry(id ?? ''),
    queryFn: () => api.get<EnquiryDetail>(`/api/v1/enquiries/${id}`),
    enabled: Boolean(id),
  })

export const useSaveEnquiry = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id
        ? api.put<EnquiryDetail>(`/api/v1/enquiries/${id}`, body)
        : api.post<EnquiryDetail>('/api/v1/enquiries', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['enquiries'] }),
  })
}

export const useChangeEnquiryStatus = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.post<EnquiryDetail>(`/api/v1/enquiries/${id}/status`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.enquiry(variables.id) })
      queryClient.invalidateQueries({ queryKey: ['enquiries'] })
    },
  })
}

export const useScheduleFollowup = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.post<Followup>(`/api/v1/enquiries/${id}/followups`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.enquiry(variables.id) })
      queryClient.invalidateQueries({ queryKey: keys.followupsDue })
    },
  })
}

export const useCompleteFollowup = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ followupId, body }: { followupId: string; body: Record<string, unknown> }) =>
      api.post<Followup>(`/api/v1/enquiries/followups/${followupId}/complete`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enquiry'] })
      queryClient.invalidateQueries({ queryKey: keys.followupsDue })
    },
  })
}

export const useDueFollowups = (filters?: { assignedTo?: string; until?: string }) =>
  useQuery({
    queryKey: [...keys.followupsDue, filters ?? {}] as const,
    queryFn: () => api.get<Followup[]>('/api/v1/enquiries/followups/due', filters),
  })

/** The Tamil date of every day in a window, for the desk calendar. */
export const useTamilDates = (from: string, to: string) =>
  useQuery({
    queryKey: ['tamil-dates', from, to] as const,
    queryFn: () => api.get<TamilDate[]>('/api/v1/tamil-dates', { from, to }),
    // The Tamil calendar does not change, so it need not be refetched.
    staleTime: Infinity,
  })

/** Auspicious dates in a window, drawn on the booking calendar. */
export const useMuhurthamDates = (from: string, to: string) =>
  useQuery({
    queryKey: ['muhurtham-dates', from, to] as const,
    queryFn: () => api.get<MuhurthamDate[]>('/api/v1/muhurtham-dates', { from, to }),
    staleTime: 60 * 60 * 1000,
  })

/**
 * Loads a muhurtham list pasted from a panchangam. Returns what was read and
 * what could not be, so the person pasting sees both.
 */
export const useImportMuhurthams = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { text: string; source?: string; occasionType?: string }) =>
      api.post<MuhurthamImportResult>('/api/v1/muhurtham-dates/import', body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['muhurtham-dates'] }),
  })
}

/** Enters one muhurtham date by hand. Re-entering a date updates it. */
export const useSaveMuhurtham = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<MuhurthamDate>('/api/v1/muhurtham-dates', body),
    onSuccess: () => client.invalidateQueries({ queryKey: ['muhurtham-dates'] }),
  })
}

/** The twenty seven stars, so a form offers them rather than have them spelt. */
export const useNakshatras = () =>
  useQuery({
    queryKey: ['nakshatras'] as const,
    queryFn: () => api.get<string[]>('/api/v1/nakshatras'),
    staleTime: Infinity,
  })

export const useDeleteMuhurtham = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/muhurtham-dates/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['muhurtham-dates'] }),
  })
}

/** Events in a date window, for the calendar. */
export const useOrderCalendar = (from: string, to: string) =>
  useQuery({
    queryKey: ['orders', 'calendar', from, to] as const,
    queryFn: () => api.get<OrderSummary[]>('/api/v1/orders/calendar', { from, to }),
  })

export const useLeadSources = () =>
  useQuery({
    queryKey: ['lead-sources'],
    queryFn: () => api.get<ReferenceItem[]>('/api/v1/enquiries/lead-sources'),
    staleTime: 30 * 60 * 1000,
  })

export const useLostReasons = () =>
  useQuery({
    queryKey: ['lost-reasons'],
    queryFn: () => api.get<ReferenceItem[]>('/api/v1/enquiries/lost-reasons'),
    staleTime: 30 * 60 * 1000,
  })

// -------------------------------------------------------- menu and packages

export const useMenuCategories = () =>
  useQuery({
    queryKey: keys.menuCategories,
    queryFn: () => api.get<MenuCategory[]>('/api/v1/menu/categories'),
    staleTime: 10 * 60 * 1000,
  })

/**
 * The catalogue, searched on the server.
 *
 * <p>Holds the page it already has while the next one is fetched. Without
 * that, changing the search empties the results for as long as the request
 * takes - and a picker whose options vanish on every keystroke reads as a
 * search that does not work at all.</p>
 */
export const useMenuItems = (filters: Record<string, unknown>) =>
  useQuery({
    queryKey: keys.menuItems(filters),
    queryFn: () =>
      api.get<PageResponse<MenuItem>>(
        '/api/v1/menu/items',
        filters as Record<string, string | number | undefined>,
      ),
    placeholderData: (previous) => previous,
  })

/**
 * The packages this company sells, narrowed the way the catalogue screen asks.
 *
 * <p>Narrowed on the server, which already knows what "for this branch" means:
 * a package tied to no branch is sold at every branch and answers for all of
 * them, and one sold at any session answers whichever session is asked for.
 * Filtering in the browser would have to repeat those rules and would drift
 * from them.</p>
 */
export const usePackages = (
  filters: {
    serviceType?: string
    sessionType?: string
    outletId?: string
    /** The space being booked. A package naming no hall is sold in every hall. */
    hallId?: string
    /**
     * Only the packages that name what was asked for. Without it the answer is
     * what may be sold at that branch, which includes the menus sold at every
     * branch - a different question, and the one a quotation asks.
     */
    exact?: boolean
  } = {},
) =>
  useQuery({
    queryKey: keys.packages(filters),
    queryFn: () => api.get<CateringPackage[]>('/api/v1/packages', filters),
  })

/**
 * The printed menu sheet for one line of business.
 *
 * <p>Every package with its price and its lines, at the version the office is
 * working from, together with the terms it is sold on - one call, because they
 * are printed as one document.</p>
 */
export const usePackageSlip = (serviceType?: string) =>
  useQuery({
    queryKey: keys.packageSlip(serviceType ?? ''),
    queryFn: () => api.get<MenuSheet>('/api/v1/packages/slip', { serviceType: serviceType! }),
    enabled: Boolean(serviceType),
  })

export const usePackageVersion = (versionId?: string) =>
  useQuery({
    queryKey: keys.packageVersion(versionId ?? ''),
    queryFn: () => api.get<PackageVersion>(`/api/v1/packages/versions/${versionId}`),
    enabled: Boolean(versionId),
  })

export const useCalculatePrice = () =>
  useMutation({
    mutationFn: ({
      packageId,
      body,
    }: {
      packageId: string
      body: {
        packageVersionId: string
        guestCount: number
        childGuestCount?: number
        outletId?: string
        eventDate?: string
        hallRentalAmount?: number
        discountAmount?: number
        discountPercent?: number
        selections: SelectionItem[]
      }
    }) => api.post<PriceCalculation>(`/api/v1/packages/${packageId}/calculate-price`, body),
  })

// --------------------------------------------------------------- quotations

export const useQuotations = (filters: Record<string, unknown>) => {
  const scoped = { ...filters, outletId: useScopedOutlet(filters.outletId as string | undefined) }
  return useQuery({
    queryKey: keys.quotations(scoped),
    queryFn: () =>
      api.get<PageResponse<QuotationSummary>>(
        '/api/v1/quotations',
        scoped as Record<string, string | number | undefined>,
      ),
  })
}

export const useQuotation = (id?: string) =>
  useQuery({
    queryKey: keys.quotation(id ?? ''),
    queryFn: () => api.get<QuotationDetail>(`/api/v1/quotations/${id}`),
    enabled: Boolean(id),
    // A revision reopens the builder straight off this, and the default
    // thirty-second staleness would let a save from minutes ago - another
    // tab, or this one, reopened later - be redrawn from what was here
    // before it, silently undoing it the moment this is saved again.
    refetchOnMount: 'always',
  })

export const useCreateQuotation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<QuotationDetail>('/api/v1/quotations', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quotations'] }),
  })
}

/** Revises a quotation: a new version, the old one marked REVISED. */
export const useReviseQuotation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.post<QuotationDetail>(`/api/v1/quotations/${id}/versions`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.quotation(variables.id) })
      queryClient.invalidateQueries({ queryKey: ['quotations'] })
    },
  })
}

/** Prices a draft quotation without saving it. */
export const usePreviewQuotation = () =>
  useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<QuotationPreview>('/api/v1/quotations/preview', body),
  })

export const useQuotationAction = (action: 'submit-approval' | 'approve' | 'send' | 'confirm-acceptance') => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body?: Record<string, unknown> }) =>
      api.post(`/api/v1/quotations/${id}/${action}`, body ?? {}),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.quotation(variables.id) })
      queryClient.invalidateQueries({ queryKey: ['quotations'] })
    },
  })
}

export const useConvertToOrder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ quotationId, body }: { quotationId: string; body?: Record<string, unknown> }) =>
      api.post<OrderDetail>(`/api/v1/quotations/${quotationId}/convert-to-order`, body ?? {}, {
        idempotencyKey: `convert-${quotationId}`,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotations'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
  })
}

// ------------------------------------------------------------------- orders

export const useOrders = (filters: Record<string, unknown>, enabled = true) => {
  const scoped = { ...filters, outletId: useScopedOutlet(filters.outletId as string | undefined) }
  return useQuery({
    queryKey: keys.orders(scoped),
    queryFn: () =>
      api.get<PageResponse<OrderSummary>>(
        '/api/v1/orders',
        scoped as Record<string, string | number | undefined>,
      ),
    enabled,
  })
}

export const useOrder = (id?: string) =>
  useQuery({
    queryKey: keys.order(id ?? ''),
    queryFn: () => api.get<OrderDetail>(`/api/v1/orders/${id}`),
    enabled: Boolean(id),
    // The revise wizard reopens straight off this. The default thirty-second
    // staleness would let it redraw from whatever was cached before - a save
    // just made, from this tab or another - and saving again from that would
    // silently undo it.
    refetchOnMount: 'always',
  })

export const useOperationalSummary = (id?: string) =>
  useQuery({
    queryKey: keys.operationalSummary(id ?? ''),
    queryFn: () => api.get<OperationalSummary>(`/api/v1/orders/${id}/operational-summary`),
    enabled: Boolean(id),
  })

/**
 * An order taken without a quotation being sent.
 *
 * <p>The priced record behind it is still raised and kept on file, so the
 * figures on the order can be traced to something.</p>
 */
export const useCreateDirectOrder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: unknown) => api.post<OrderDetail>('/api/v1/orders/direct', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['quotations'] })
    },
  })
}

export const useOrderAction = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      action,
      body,
      params,
    }: {
      id: string
      action: string
      body?: Record<string, unknown>
      params?: Record<string, string | number | boolean>
    }) => {
      const query = params
        ? `?${new URLSearchParams(
            Object.entries(params).map(([key, value]) => [key, String(value)]),
          ).toString()}`
        : ''
      return api.post<OrderDetail>(`/api/v1/orders/${id}/${action}${query}`, body)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.id) })
      queryClient.invalidateQueries({ queryKey: keys.operationalSummary(variables.id) })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      // Confirming an order, and drawing its list again, both put work in
      // front of the kitchen. A board still showing the old answer is the
      // reason somebody rings up to ask whether it went through.
      queryClient.invalidateQueries({ queryKey: ['kitchen'] })
    },
  })
}

/** Corrects an order's details. The date, hall and head count are not here. */
export const useEditOrder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.put<OrderDetail>(`/api/v1/orders/${id}`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.id) })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
  })
}

/**
 * Corrects the order wholesale - sessions, menu and discount together, the
 * same act as revising a quotation. Reaches past the change-request desk.
 */
export const useReviseOrder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.put<OrderDetail>(`/api/v1/orders/${id}/revise`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.id) })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['kitchen'] })
    },
  })
}

/**
 * Money off an order, and the words it is sold on.
 *
 * <p>Parked rather than applied where the discount is beyond what the caller
 * may agree - the same authority a discount on a quotation is held to - and
 * let through from {@link useDecideOrderDiscountApproval} once whoever holds
 * that authority says so.</p>
 */
export const useApplyOrderCharges = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, body }: { orderId: string; body: Record<string, unknown> }) =>
      api.post<OrderDetail>(`/api/v1/orders/${orderId}/charges`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
  })
}

/** Lets through, or turns down, a discount parked on an order beyond the preparer's own authority. */
export const useDecideOrderDiscountApproval = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, approved, remarks }: { orderId: string; approved: boolean; remarks?: string }) =>
      api.post<OrderDetail>(`/api/v1/orders/${orderId}/discount-approval`, { approved, remarks }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: keys.approvals })
    },
  })
}

/**
 * Forces an order's own total to a figure named directly, past every
 * mechanism that would otherwise work it out. Refused to anyone but a
 * super admin.
 */
export const useForceOrderValue = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, totalAmount, reason }: { orderId: string; totalAmount: number; reason: string }) =>
      api.post<OrderDetail>(`/api/v1/orders/${orderId}/force-value`, { totalAmount, reason }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['amenities-pending', variables.orderId] })
    },
  })
}

/**
 * Corrects an order's own total to agree with what its sessions and extras
 * come to today, read fresh rather than trusted to a chain of past edits.
 * For a total that drifted because of a pricing bug since fixed, not for a
 * figure that is still wrong today. Refused to anyone but a super admin.
 */
export const useRecalculateOrderTotal = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (orderId: string) =>
      api.post<OrderDetail>(`/api/v1/orders/${orderId}/recalculate-total`, {}),
    onSuccess: (_data, orderId) => {
      queryClient.invalidateQueries({ queryKey: keys.order(orderId) })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['amenities-pending', orderId] })
    },
  })
}

/**
 * Adds a sitting to an order that was taken without one.
 *
 * <p>The hall is checked as the sitting goes in, which is when it starts being
 * occupied - an order taken at the counter holds nothing until then.</p>
 */
export const useAddOrderSession = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, body }: { orderId: string; body: Record<string, unknown> }) =>
      api.post<OrderDetail>(`/api/v1/orders/${orderId}/sessions`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: keys.operationalSummary(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: ['availability'] })
      queryClient.invalidateQueries({ queryKey: ['hall-slots'] })
      queryClient.invalidateQueries({ queryKey: ['amenities-pending', variables.orderId] })
    },
  })
}

/** Moves one sitting; the hall is re-checked whenever its times or hall change. */
export const useEditOrderSession = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, sessionId, body }: {
      orderId: string
      sessionId: string
      body: Record<string, unknown>
    }) => api.put<OrderDetail>(`/api/v1/orders/${orderId}/sessions/${sessionId}`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: keys.operationalSummary(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: ['availability'] })
      queryClient.invalidateQueries({ queryKey: ['hall-slots'] })
      queryClient.invalidateQueries({ queryKey: ['amenities-pending', variables.orderId] })
    },
  })
}

/**
 * Takes a sitting off an order.
 *
 * <p>The hall it was holding goes back to the diary and what it was sold for
 * comes off the order, so the availability board and the order both have to be
 * read again.</p>
 */
/** The kitchen's sheet for one order, in both scripts. */
export const useKitchenSlip = (orderId?: string) =>
  useQuery({
    queryKey: ['kitchen-slip', orderId ?? ''] as const,
    queryFn: () => api.get<KitchenSlip>(`/api/v1/orders/${orderId}/kitchen-slip`),
    enabled: Boolean(orderId),
  })

export const useRemoveOrderSession = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, sessionId, reason }: {
      orderId: string
      sessionId: string
      reason?: string
    }) =>
      api.delete<OrderDetail>(
        `/api/v1/orders/${orderId}/sessions/${sessionId}`,
        reason ? { reason } : undefined,
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: keys.operationalSummary(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: ['availability'] })
      queryClient.invalidateQueries({ queryKey: ['hall-slots'] })
      queryClient.invalidateQueries({ queryKey: ['amenities-pending', variables.orderId] })
    },
  })
}

/** Adds a dish to what the kitchen will cook. Refused once the menu is frozen. */
export const useAddOrderMenuItem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, body }: { orderId: string; body: Record<string, unknown> }) =>
      api.post<OrderDetail>(`/api/v1/orders/${orderId}/menu-items`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: keys.operationalSummary(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: ['kitchen'] })
    },
  })
}

/** Puts a priced item on the order; it is billed on the final invoice. */
export const useAddOrderItem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, body }: { orderId: string; body: Record<string, unknown> }) =>
      api.post<OrderDetail>(`/api/v1/orders/${orderId}/items`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: keys.orders({}) })
    },
  })
}

/** Corrects a line already on the order. Refused once it has been invoiced. */
export const useUpdateOrderItem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, itemId, body }:
      { orderId: string; itemId: string; body: Record<string, unknown> }) =>
      api.put<OrderDetail>(`/api/v1/orders/${orderId}/items/${itemId}`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: keys.orders({}) })
    },
  })
}

export const useRemoveOrderItem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, itemId }: { orderId: string; itemId: string }) =>
      api.delete<OrderDetail>(`/api/v1/orders/${orderId}/items/${itemId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: keys.orders({}) })
    },
  })
}

/**
 * Changes a dish already on the menu.
 *
 * <p>Where the customer has confirmed the order, the server raises this as a
 * change for them to agree to rather than moving the line, and the order comes
 * back with the change pending on it.</p>
 */
export const useUpdateOrderMenuItem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, rowId, body }:
      { orderId: string; rowId: string; body: Record<string, unknown> }) =>
      api.put<OrderDetail>(`/api/v1/orders/${orderId}/menu-items/${rowId}`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: keys.operationalSummary(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: ['order-changes'] })
      queryClient.invalidateQueries({ queryKey: ['kitchen'] })
    },
  })
}

export const useRemoveOrderMenuItem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, rowId }: { orderId: string; rowId: string }) =>
      api.delete<OrderDetail>(`/api/v1/orders/${orderId}/menu-items/${rowId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: keys.operationalSummary(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: ['kitchen'] })
    },
  })
}

/**
 * Moves a whole booking to another day.
 *
 * <p>Every sitting travels together and each hall is re-checked on the new
 * date, so a clash comes back as a refusal rather than a double booking.</p>
 */
export const usePostponeOrder = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, body }: { orderId: string; body: Record<string, unknown> }) =>
      api.post<OrderDetail>(`/api/v1/orders/${orderId}/postpone`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: keys.orders({}) })
      queryClient.invalidateQueries({ queryKey: ['availability'] })
      queryClient.invalidateQueries({ queryKey: ['kitchen'] })
      queryClient.invalidateQueries({ queryKey: keys.operationalSummary(variables.orderId) })
    },
  })
}

export const useOrderStatusChange = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status, remarks }: { id: string; status: OrderStatus; remarks?: string }) =>
      api.post<OrderDetail>(`/api/v1/orders/${id}/status`, { status, remarks }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.id) })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
  })
}

/**
 * Moves an order to the customer it should have been taken against.
 *
 * <p>Everything raised on it moves too - bills, receipts, credit notes,
 * refunds and what each of the two is shown as owing - so every list that
 * counts money by customer is asked again afterwards.</p>
 */
export const useMoveOrderCustomer = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, customerId, reason }: { id: string; customerId: string; reason: string }) =>
      api.post<MovedCustomer>(`/api/v1/orders/${id}/customer`, { customerId, reason }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.id) })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['payments'] })
      queryClient.invalidateQueries({ queryKey: ['customer'] })
    },
  })
}

/** Gives the booking away, or charges for it after all. */
export const useMarkComplimentary = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, complimentary }: { id: string; complimentary: boolean }) =>
      api.post<OrderDetail>(
        `/api/v1/orders/${id}/complimentary?complimentary=${complimentary}`, {}),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.order(variables.id) })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

export const useRaiseChangeRequest = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.post(`/api/v1/orders/${id}/change-requests`, body),
    onSuccess: (_data, variables) =>
      queryClient.invalidateQueries({ queryKey: keys.order(variables.id) }),
  })
}

// ------------------------------------------------------------------ kitchen

export const useKitchenBoard = (
  filters: { kitchenId?: string; status?: string; mealPeriod?: string; from: string; to: string },
) =>
  useQuery({
    queryKey: keys.kitchenBoard(filters),
    queryFn: () => api.get<KitchenRequest[]>('/api/v1/kitchen/requests', filters),
  })

/**
 * What the kitchen has to cook. Narrow it by branch, hall, course or sitting -
 * the whole day's board is more than one shift can work from.
 */
export const useConsolidatedProduction = (filters: {
  kitchenId?: string
  productionDate: string
  toDate?: string
  mealPeriod?: string
  outletId?: string
  hallId?: string
  categoryId?: string
}) =>
  useQuery({
    queryKey: keys.kitchenConsolidated(filters),
    queryFn: () => api.get<ConsolidatedItem[]>('/api/v1/kitchen/production-summary', filters),
  })

export const useKitchenAction = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, action, body }: { id: string; action: string; body?: Record<string, unknown> }) =>
      api.post(`/api/v1/kitchen/requests/${id}/${action}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kitchen'] }),
  })
}

// ----------------------------------------------------------------- manpower

export const useManpowerRequirements = (orderId?: string) =>
  useQuery({
    queryKey: keys.manpower(orderId ?? ''),
    queryFn: () => api.get<ManpowerRequirement[]>(`/api/v1/orders/${orderId}/manpower-requirements`),
    enabled: Boolean(orderId),
  })

/**
 * Puts a designation on the job that no rule derived - a cleaner, a second
 * driver. The rules follow from the covers and the counters and know nothing
 * about either.
 */
export const useAddManpowerRequirement = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, staffRole, requiredCount, orderEventId }: {
      orderId: string
      staffRole: string
      requiredCount: number
      orderEventId?: string
    }) => api.post(`/api/v1/orders/${orderId}/manpower-requirements`,
      { staffRole, requiredCount, orderEventId }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.manpower(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: keys.operationalSummary(variables.orderId) })
    },
  })
}

/**
 * Moves a requirement off the headcount the rules derived. The reason is kept
 * with it: a variance above the configured threshold goes for approval, and
 * whoever approves it needs to know why the office asked for more.
 */
export const useAdjustManpowerRequirement = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ requirementId, requiredCount, reason }: {
      requirementId: string
      orderId: string
      requiredCount: number
      reason?: string
    }) => api.put(`/api/v1/manpower-requirements/${requirementId}`, { requiredCount, reason }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: keys.manpower(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: keys.operationalSummary(variables.orderId) })
      queryClient.invalidateQueries({ queryKey: keys.approvals })
    },
  })
}

export const useStaff = (filters: Record<string, unknown>) => {
  const scoped = { ...filters, outletId: useScopedOutlet(filters.outletId as string | undefined) }
  return useQuery({
    queryKey: keys.staff(scoped),
    queryFn: () =>
      api.get<StaffMember[]>('/api/v1/staff', scoped as Record<string, string | undefined>),
  })
}

const useStaffMutation = <TVariables,>(fn: (variables: TVariables) => Promise<unknown>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staff'] }),
  })
}

export const useCreateStaff = () =>
  useStaffMutation((body: Record<string, unknown>) =>
    api.post<StaffMember>('/api/v1/staff', body),
  )

export const useUpdateStaff = () =>
  useStaffMutation(({ id, body }: { id: string; body: Record<string, unknown> }) =>
    api.put<StaffMember>(`/api/v1/staff/${id}`, body),
  )

export const useAllocateStaff = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ requirementId, staffIds }: { requirementId: string; staffIds: string[] }) =>
      api.post(`/api/v1/manpower-requirements/${requirementId}/allocations`, { staffIds }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['manpower'] }),
  })
}

// ---------------------------------------------------------------- logistics

export const useVehicles = (filters: Record<string, unknown>) =>
  useQuery({
    queryKey: keys.vehicles(filters),
    queryFn: () =>
      api.get<Vehicle[]>('/api/v1/vehicles', filters as Record<string, string | undefined>),
  })

/** Every vehicle mutation refreshes the register and the trips that name one. */
const useVehicleMutation = <TVariables,>(fn: (variables: TVariables) => Promise<unknown>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] })
      queryClient.invalidateQueries({ queryKey: ['trips'] })
    },
  })
}

export const useCreateVehicle = () =>
  useVehicleMutation((body: Record<string, unknown>) =>
    api.post<Vehicle>('/api/v1/vehicles', body),
  )

export const useUpdateVehicle = () =>
  useVehicleMutation(({ id, body }: { id: string; body: Record<string, unknown> }) =>
    api.put<Vehicle>(`/api/v1/vehicles/${id}`, body),
  )

export const useTrips = (filters: { status?: string; from: string; to: string }) =>
  useQuery({
    queryKey: keys.trips(filters),
    queryFn: () => api.get<Trip[]>('/api/v1/trips', filters),
  })

export const useUpdateTripStatus = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.post(`/api/v1/trips/${id}/status`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trips'] }),
  })
}

// ------------------------------------------------------------------ finance

export const useInvoices = (filters: Record<string, unknown>) => {
  const scoped = { ...filters, outletId: useScopedOutlet(filters.outletId as string | undefined) }
  return useQuery({
    queryKey: keys.invoices(scoped),
    queryFn: () =>
      api.get<PageResponse<Invoice>>(
        '/api/v1/invoices',
        scoped as Record<string, string | number | undefined>,
      ),
  })
}

/**
 * Every bill still owed, by customer or by branch.
 *
 * <p>What the counter needs in front of it before taking money. A regular
 * customer with three events running has three bills open, and the money
 * handed over is usually one payment covering all of them.</p>
 */
export const useOutstandingInvoices = (params: {
  customerId?: string
  outletId?: string
  enabled?: boolean
}) =>
  useQuery({
    queryKey: ['invoices', 'outstanding', params.customerId ?? '', params.outletId ?? ''],
    queryFn: () =>
      api.get<Invoice[]>('/api/v1/invoices/outstanding', {
        customerId: params.customerId,
        outletId: params.outletId,
      }),
    enabled: params.enabled ?? true,
  })

export const usePayments = (filters: Record<string, unknown>) => {
  const scoped = { ...filters, outletId: useScopedOutlet(filters.outletId as string | undefined) }
  return useQuery({
    queryKey: keys.payments(scoped),
    queryFn: () =>
      api.get<PageResponse<Payment>>(
        '/api/v1/payments',
        scoped as Record<string, string | number | undefined>,
      ),
  })
}

/** Every receipt taken against this order, newest first. */
export const useOrderPayments = (orderId?: string) =>
  useQuery({
    queryKey: ['payments', 'order', orderId] as const,
    queryFn: () => api.get<Payment[]>(`/api/v1/orders/${orderId}/payments`),
    enabled: Boolean(orderId),
  })

export const usePaymentSchedule = (orderId?: string) =>
  useQuery({
    queryKey: keys.paymentSchedule(orderId ?? ''),
    queryFn: () => api.get<PaymentSchedule[]>(`/api/v1/orders/${orderId}/payment-schedule`),
    enabled: Boolean(orderId),
  })

/**
 * Corrects a receipt that was written down wrong. The amount can only be
 * corrected while none of it has been set against a bill; after that the
 * receipt is reversed and taken again.
 */
export const useUpdatePayment = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.put<Payment>(`/api/v1/payments/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      // A receipt moved onto another order moves that order's own figures.
      queryClient.invalidateQueries({ queryKey: ['order'] })
      queryClient.invalidateQueries({ queryKey: ['payment-schedule'] })
      queryClient.invalidateQueries({ queryKey: keys.outstanding })
      queryClient.invalidateQueries({ queryKey: ['refundable'] })
      queryClient.invalidateQueries({ queryKey: ['amenities-pending'] })
    },
  })
}

export const useRecordPayment = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ body, idempotencyKey }: { body: Record<string, unknown>; idempotencyKey: string }) =>
      api.post<Payment>('/api/v1/payments', body, { idempotencyKey }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payments'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order'] })
      // The advance settles milestones and can confirm the order outright.
      queryClient.invalidateQueries({ queryKey: ['payment-schedule'] })
      queryClient.invalidateQueries({ queryKey: keys.outstanding })
      queryClient.invalidateQueries({ queryKey: ['amenities-pending'] })
    },
  })
}

/** The excess on one order, so the office is shown the figure not asked for it. */
export const useRefundable = (orderId?: string) =>
  useQuery({
    queryKey: ['refundable', orderId],
    queryFn: () => api.get<Refundable>(`/api/v1/orders/${orderId}/refundable`),
    enabled: Boolean(orderId),
  })

/** Raising a refund by hand against an order's excess. */
export const useRequestRefund = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, body }: { orderId: string; body: unknown }) =>
      api.post<Refund>(`/api/v1/orders/${orderId}/refunds`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['refunds'] })
      queryClient.invalidateQueries({ queryKey: ['refundable'] })
    },
  })
}

export const useRefunds = (status?: string) =>
  useQuery({
    queryKey: ['refunds', status ?? 'all'],
    queryFn: () => api.get<Refund[]>('/api/v1/refunds', { status }),
  })

/** Agreeing a refund, or turning it down. */
export const useDecideRefund = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) =>
      api.post<Refund>(`/api/v1/refunds/${id}/decision`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['refunds'] }),
  })
}

/** What an order is still short by, and so what may be written off. */
export const useWriteOffSuggestion = (orderId?: string) =>
  useQuery({
    queryKey: ['write-off', orderId],
    queryFn: () => api.get<WriteOffSuggestion>(`/api/v1/orders/${orderId}/write-off`),
    enabled: Boolean(orderId),
  })

/** Every discount, rebate and write-off raised against one order. */
export const useOrderCreditNotes = (orderId?: string) =>
  useQuery({
    queryKey: ['credit-notes', 'order', orderId],
    queryFn: () => api.get<CreditNote[]>(`/api/v1/orders/${orderId}/credit-notes`),
    enabled: Boolean(orderId),
  })

export const useCreditNotes = (status?: string) =>
  useQuery({
    queryKey: ['credit-notes', status ?? 'all'],
    queryFn: () => api.get<CreditNote[]>('/api/v1/credit-notes', { status }),
  })

/** Letting part of a balance go. It waits for approval before anything moves. */
export const useRequestWriteOff = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ orderId, body }: { orderId: string; body: unknown }) =>
      api.post<CreditNote>(`/api/v1/orders/${orderId}/write-off`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-notes'] })
      queryClient.invalidateQueries({ queryKey: ['write-off'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
  })
}

/** Agreeing one, or turning it down. */
export const useDecideWriteOff = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) =>
      api.post<CreditNote>(`/api/v1/credit-notes/${id}/decision`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-notes'] })
      queryClient.invalidateQueries({ queryKey: ['write-off'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
  })
}

/** The voucher for a discount, rebate or write-off, sent to the printer. */
export const printWriteOffVoucher = (note: { id: string; creditNoteNumber: string }) =>
  api.print(`/api/v1/credit-notes/${note.id}/pdf`)

/** The same voucher kept as a file. */
export const downloadWriteOffVoucher = (note: { id: string; creditNoteNumber: string }) =>
  api.download(
    `/api/v1/credit-notes/${note.id}/pdf`,
    undefined,
    `${note.creditNoteNumber.replace(/\//g, '-')}.pdf`,
  )

/** Every refund raised against one order, so the voucher is where the order is. */
export const useOrderRefunds = (orderId?: string) =>
  useQuery({
    queryKey: ['refunds', 'order', orderId],
    queryFn: () => api.get<Refund[]>(`/api/v1/orders/${orderId}/refunds`),
    enabled: Boolean(orderId),
  })

/** The refund voucher, sent to the printer: it is signed by whoever releases it. */
export const printRefundVoucher = (refund: { id: string; refundNumber: string }) =>
  api.print(`/api/v1/refunds/${refund.id}/pdf`)

/** The same voucher kept as a file. */
export const downloadRefundVoucher = (refund: { id: string; refundNumber: string }) =>
  api.download(
    `/api/v1/refunds/${refund.id}/pdf`,
    undefined,
    `${refund.refundNumber.replace(/\//g, '-')}.pdf`,
  )

/** Undoing a refund raised in error. It stays on file with the reason. */
export const useReverseRefund = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<Refund>(`/api/v1/refunds/${id}/reverse`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['refunds'] })
      queryClient.invalidateQueries({ queryKey: ['refundable'] })
    },
  })
}

/** Recording that the money actually left, and by what reference. */
export const useProcessRefund = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: unknown }) =>
      api.post<Refund>(`/api/v1/refunds/${id}/process`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['refunds'] }),
  })
}

export const useReversePayment = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post<Payment>(`/api/v1/payments/${id}/reverse`, { reason }),
    onSuccess: () => {
      // A reversal moves exactly the same figures recording a receipt does -
      // the order it was against, whatever bill it was allocated to, the
      // schedule it may have settled - so it invalidates the same keys.
      queryClient.invalidateQueries({ queryKey: ['payments'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['order'] })
      queryClient.invalidateQueries({ queryKey: ['payment-schedule'] })
      queryClient.invalidateQueries({ queryKey: keys.outstanding })
      queryClient.invalidateQueries({ queryKey: ['refundable'] })
      queryClient.invalidateQueries({ queryKey: ['amenities-pending'] })
    },
  })
}

/** Pass enabled=false for a role without billing.read, so it is not requested. */
export const useOutstandingReport = (enabled = true) =>
  useQuery({
    queryKey: keys.outstanding,
    queryFn: () => api.get<CustomerOutstanding[]>('/api/v1/reports/outstanding'),
    enabled,
  })

// ----------------------------------------------------------------- feedback

export const useFeedback = (filters: Record<string, unknown>) => {
  const scoped = { ...filters, outletId: useScopedOutlet(filters.outletId as string | undefined) }
  return useQuery({
    queryKey: keys.feedback(scoped),
    queryFn: () =>
      api.get<PageResponse<FeedbackView>>(
        '/api/v1/feedback',
        scoped as Record<string, string | number | undefined>,
      ),
  })
}

export const useFeedbackSummary = (outletId?: string) => {
  const scoped = useScopedOutlet(outletId)
  return useQuery({
    queryKey: keys.feedbackSummary(scoped),
    queryFn: () => api.get<FeedbackSummary>('/api/v1/feedback/summary', { outletId: scoped }),
  })
}

export const useRecoveryCases = (status?: string) => {
  const outletId = useScopedOutlet()
  return useQuery({
    queryKey: [...keys.recoveryCases(status), outletId ?? ''] as const,
    queryFn: () => api.get<RecoveryCase[]>('/api/v1/service-recovery-cases', { status, outletId }),
  })
}

export const useFeedbackAttachments = (feedbackId: string) =>
  useQuery({
    queryKey: ['feedback-attachments', feedbackId] as const,
    queryFn: () => api.get<FeedbackAttachment[]>(`/api/v1/feedback/${feedbackId}/attachments`),
  })

export const useFeedbackContact = (orderId?: string, enabled = true) =>
  useQuery({
    queryKey: ['feedback-contact', orderId ?? ''] as const,
    queryFn: () => api.get<FeedbackContact>(`/api/v1/orders/${orderId}/feedback-request`),
    enabled: Boolean(orderId) && enabled,
  })

export const useSendFeedbackRequest = () =>
  useMutation({
    mutationFn: ({ orderId, ...body }: {
      orderId: string
      channels: ('EMAIL' | 'WHATSAPP')[]
      email?: string
      mobile?: string
      subject?: string
      message?: string
      includeLink: boolean
    }) => api.post<FeedbackLinkSent[]>(`/api/v1/orders/${orderId}/feedback-request`, body),
  })

export const useUpdateRecoveryCase = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch<RecoveryCase>(`/api/v1/service-recovery-cases/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['recovery-cases'] }),
  })
}

// --------------------------------------------------------------- dashboards

/** `outletId` narrows every figure to one outlet; left off, the whole company. */
export const useExecutiveDashboard = (from?: string, to?: string, outletId?: string) => {
  const scoped = useScopedOutlet(outletId)
  return useQuery({
    queryKey: keys.dashboards('executive', { from, to, outletId: scoped }),
    queryFn: () =>
      api.get<ExecutiveDashboard>('/api/v1/dashboards/executive', { from, to, outletId: scoped }),
  })
}

export const useSalesDashboard = (from?: string, to?: string, outletId?: string) => {
  const scoped = useScopedOutlet(outletId)
  return useQuery({
    queryKey: keys.dashboards('sales', { from, to, outletId: scoped }),
    queryFn: () =>
      api.get<import('./types').SalesDashboard>('/api/v1/dashboards/sales',
        { from, to, outletId: scoped }),
  })
}

export const useOperationsDashboard = (outletId?: string) => {
  const scoped = useScopedOutlet(outletId)
  return useQuery({
    queryKey: keys.dashboards('operations', { outletId: scoped }),
    queryFn: () =>
      api.get<OperationsDashboard>('/api/v1/dashboards/operations', { outletId: scoped }),
    refetchInterval: 60_000,
  })
}

export const useKitchenDashboard = (productionDate: string, kitchenId?: string) =>
  useQuery({
    queryKey: keys.dashboards('kitchen', { productionDate, kitchenId }),
    queryFn: () => api.get<KitchenDashboard>('/api/v1/dashboards/kitchen', { productionDate, kitchenId }),
    refetchInterval: 60_000,
  })

export const useFinanceDashboard = (from?: string, to?: string, outletId?: string) => {
  const scoped = useScopedOutlet(outletId)
  return useQuery({
    queryKey: keys.dashboards('finance', { from, to, outletId: scoped }),
    queryFn: () =>
      api.get<FinanceDashboard>('/api/v1/dashboards/finance', { from, to, outletId: scoped }),
  })
}

// ------------------------------------------------------- approvals and inbox

export const usePendingApprovals = () =>
  useQuery({
    queryKey: keys.approvals,
    queryFn: () => api.get<PendingApproval[]>('/api/v1/approvals/pending'),
  })

export const useDecideApproval = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, approved, remarks }: { id: string; approved: boolean; remarks?: string }) =>
      api.post(`/api/v1/approvals/${id}/decision`, { approved, remarks }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.approvals }),
  })
}

// ------------------------------------------------------- document numbering

const numberingKey = ['number-series'] as const

export const useNumberSeries = () =>
  useQuery({
    queryKey: numberingKey,
    queryFn: () => api.get<NumberingSettings>('/api/v1/number-series'),
  })

/**
 * Saving a series only changes what future numbers look like. Every list that
 * shows a document number is left alone, because numbers already issued keep
 * the form they were issued in.
 */
export const useSaveNumberSeries = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      sequenceType: string
      prefix: string
      padding: number
      resetMode: string
      nextNumber: number
    }) =>
      api.put<NumberSeriesView>('/api/v1/number-series', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: numberingKey }),
  })
}

export const useSaveNumberingResetDate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { month: number; day: number }) =>
      api.put<NumberingSettings>('/api/v1/number-series/reset-date', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: numberingKey }),
  })
}

// ------------------------------------------------------------------ policies

const policiesKey = ['policies'] as const

export const usePolicies = () =>
  useQuery({
    queryKey: policiesKey,
    queryFn: () => api.get<PolicySettings>('/api/v1/policies'),
  })

/**
 * Saving terms changes what is charged and when money falls due from here on.
 * Orders already cancelled keep the charge they were cancelled on, and payment
 * schedules already raised are not rebuilt, so nothing behind us moves.
 */
const policyMutation = <TVariables, TResult>(fn: (input: TVariables) => Promise<TResult>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: policiesKey }),
  })
}

export const useSaveCancellationPolicy = () =>
  policyMutation((body: unknown) => api.put<CancellationPolicyView>('/api/v1/policies/cancellation', body))

export const useRemoveCancellationPolicy = () =>
  policyMutation((id: string) => api.delete<void>(`/api/v1/policies/cancellation/${id}`))

export const useSaveAdvancePolicy = () =>
  policyMutation((body: unknown) => api.put<AdvancePolicyView>('/api/v1/policies/advance', body))

export const useRemoveAdvancePolicy = () =>
  policyMutation((id: string) => api.delete<void>(`/api/v1/policies/advance/${id}`))

// --------------------------------------------------------------------- terms

const termsKey = ['terms'] as const

/** How each service and document carries its terms: words, a code, or both. */
export interface TermsDisplayRule {
  serviceType: string
  documentType: string
  showText: boolean
  showQr: boolean
}

export const useTermsDisplay = () =>
  useQuery({
    queryKey: ['terms-display'],
    queryFn: () => api.get<TermsDisplayRule[]>('/api/v1/terms/display'),
  })

export const useSaveTermsDisplay = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (rule: TermsDisplayRule) =>
      api.put<TermsDisplayRule>('/api/v1/terms/display', rule),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['terms-display'] }),
  })
}

/** Clears an explicit answer for one row, so it goes back to following the nearest rule above it. */
export const useResetTermsDisplay = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ serviceType, documentType }: { serviceType: string; documentType: string }) =>
      api.delete(`/api/v1/terms/display?serviceType=${serviceType}&documentType=${documentType}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['terms-display'] }),
  })
}

/** One discount the business gives, offered where a discount is entered. */
export interface DiscountRate {
  id: string
  percent: number
  label?: string | null
  sortOrder: number
  active: boolean
}

export const useDiscountRates = (options: { includeInactive?: boolean } = {}) =>
  useQuery({
    queryKey: ['discount-rates', options.includeInactive ?? false],
    queryFn: () =>
      api.get<DiscountRate[]>('/api/v1/discount-rates', {
        includeInactive: options.includeInactive ? 'true' : undefined,
      }),
  })

export const useSaveDiscountRate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id
        ? api.put<DiscountRate>(`/api/v1/discount-rates/${id}`, body)
        : api.post<DiscountRate>('/api/v1/discount-rates', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['discount-rates'] }),
  })
}

export const useRemoveDiscountRate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/discount-rates/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['discount-rates'] }),
  })
}

/** Tax rates this business bills on one document together. */
export type TaxRateGroup = {
  id: string
  name: string
  rates: number[]
  sortOrder: number
  active: boolean
}

export const useTaxRateGroups = (options: { includeInactive?: boolean } = {}) =>
  useQuery({
    queryKey: ['tax-rate-groups', options.includeInactive ?? false],
    queryFn: () =>
      api.get<TaxRateGroup[]>('/api/v1/tax-rate-groups', {
        includeInactive: options.includeInactive ? 'true' : undefined,
      }),
  })

export const useSaveTaxRateGroup = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id
        ? api.put<TaxRateGroup>(`/api/v1/tax-rate-groups/${id}`, body)
        : api.post<TaxRateGroup>('/api/v1/tax-rate-groups', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tax-rate-groups'] }),
  })
}

export const useRemoveTaxRateGroup = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/tax-rate-groups/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tax-rate-groups'] }),
  })
}

/**
 * The kitchens a branch cooks in.
 *
 * <p>Until a branch has one, nothing an order sells can be put in front of
 * anybody to cook: the day's requests are filed against a kitchen.</p>
 */
export type Kitchen = {
  id: string
  outletId: string
  code: string
  name: string
  kitchenType: string
  dailyCapacityPortions?: number
  active: boolean
}

export const useKitchens = (outletId?: string) =>
  useQuery({
    queryKey: ['kitchens', outletId ?? 'all'] as const,
    queryFn: () => api.get<Kitchen[]>('/api/v1/kitchens', { outletId }),
  })

export const useSaveKitchen = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: Record<string, unknown> }) =>
      id
        ? api.put<Kitchen>(`/api/v1/kitchens/${id}`, body)
        : api.post<Kitchen>('/api/v1/kitchens', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kitchens'] }),
  })
}

export const useRemoveKitchen = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/kitchens/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kitchens'] }),
  })
}

export const useServiceTerms = () =>
  useQuery({
    queryKey: termsKey,
    queryFn: () => api.get<ServiceTermsView[]>('/api/v1/terms'),
  })

/**
 * Saving clauses changes what future quotations carry. Quotations already
 * raised keep the wording they were raised on, so nothing behind us moves.
 */
export const useSaveServiceTerms = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ serviceType, terms }: { serviceType: string; terms: unknown[] }) =>
      api.put<ServiceTermsView>(`/api/v1/terms/${serviceType}`, { terms }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: termsKey }),
  })
}

// ------------------------------------------------------ users and access

const usersKey = (companyId?: string) => ['users', companyId ?? 'own'] as const
const rolesKey = ['roles'] as const

/** The people in a company: the caller's own, or another they administer. */
export const useUsers = (companyId?: string) =>
  useQuery({
    queryKey: usersKey(companyId),
    queryFn: () => api.get<AppUser[]>('/api/v1/users', { companyId }),
  })

export const useRoles = () =>
  useQuery({ queryKey: rolesKey, queryFn: () => api.get<RoleView[]>('/api/v1/roles') })

export const usePermissions = () =>
  useQuery({
    queryKey: ['permissions'] as const,
    queryFn: () => api.get<PermissionView[]>('/api/v1/permissions'),
    // The list only changes when the application itself does.
    staleTime: Infinity,
  })

/**
 * Anything that changes who may do what invalidates both lists: a role's
 * permissions change what every holder of it can reach.
 */
const accessMutation = <TVariables, TResult>(fn: (input: TVariables) => Promise<TResult>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: rolesKey })
    },
  })
}

export const useSaveUser = () =>
  accessMutation(({ id, body }: { id?: string; body: unknown }) =>
    id ? api.put<AppUser>(`/api/v1/users/${id}`, body) : api.post<AppUser>('/api/v1/users', body),
  )

export const useSetUserEnabled = () =>
  accessMutation(({ id, enabled, reason }: { id: string; enabled: boolean; reason?: string }) =>
    api.post<AppUser>(`/api/v1/users/${id}/enabled`, { enabled, reason }),
  )

/**
 * Moves somebody to another company.
 *
 * <p>Its own call rather than part of the edit, because it is not an edit: the
 * outlets they were mapped to and any role their old company wrote for itself
 * are that company's, and are left behind.</p>
 */
export const useMoveUserCompany = () =>
  accessMutation(({ id, companyId, reason }: { id: string; companyId: string; reason?: string }) =>
    api.post<AppUser>(`/api/v1/users/${id}/company`, { companyId, reason }),
  )

export const useResetUserPassword = () =>
  accessMutation(({ id, newPassword }: { id: string; newPassword: string }) =>
    api.post<AppUser>(`/api/v1/users/${id}/reset-password`, { newPassword }),
  )

export const useSaveRole = () =>
  accessMutation(({ id, body }: { id?: string; body: unknown }) =>
    id ? api.put<RoleView>(`/api/v1/roles/${id}`, body) : api.post<RoleView>('/api/v1/roles', body),
  )

/** Switches a role off, or back on. Off is refused while anyone holds it. */
export const useSetRoleEnabled = () =>
  accessMutation(({ id, enabled, reason }: { id: string; enabled: boolean; reason?: string }) =>
    api.post<RoleView>(`/api/v1/roles/${id}/enabled`, { enabled, reason }),
  )

export const useRemoveRole = () =>
  accessMutation((id: string) => api.delete<void>(`/api/v1/roles/${id}`))

export type NotificationFilters = {
  documentType?: string
  channel?: string
  status?: string
  unreadOnly?: boolean
  from?: string
  to?: string
  page?: number
  size?: number
}

export const useNotifications = (filters: NotificationFilters = {}) =>
  useQuery({
    queryKey: [...keys.notifications, filters] as const,
    queryFn: () =>
      api.get<PageResponse<NotificationView>>('/api/v1/notifications', { size: 20, ...filters }),
    refetchInterval: 60_000,
  })

/** How much is in the inbox, how much unread, and what it is about. */
export const useNotificationSummary = (range: { from?: string; to?: string } = {}) =>
  useQuery({
    queryKey: [...keys.notifications, 'summary', range] as const,
    queryFn: () => api.get<InboxSummary>('/api/v1/notifications/summary', range),
    refetchInterval: 60_000,
  })

const useNotificationMutation = <TVariables,>(fn: (variables: TVariables) => Promise<unknown>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.notifications }),
  })
}

export const useMarkNotificationRead = () =>
  useNotificationMutation((id: string) => api.post(`/api/v1/notifications/${id}/read`, {}))

/** Clears the unread mark on everything shown, or on one kind of document. */
export const useMarkAllNotificationsRead = () =>
  useNotificationMutation((documentType?: string) =>
    api.post('/api/v1/notifications/read-all', {},
      documentType ? { params: { documentType } } : undefined),
  )

// --------------------------------------------- gate passes and EB metering

type GatePassFilters = {
  status?: string
  orderId?: string
  outletId?: string
  q?: string
  page?: number
  size?: number
}

const gatePassPath = (type: 'OUTDOOR_VESSEL' | 'HALL_ASSET') =>
  type === 'OUTDOOR_VESSEL' ? '/api/v1/outpasses' : '/api/v1/hall-handovers'

export const useGatePasses = (type: 'OUTDOOR_VESSEL' | 'HALL_ASSET', filters: GatePassFilters) =>
  useQuery({
    queryKey: ['gate-passes', type, filters] as const,
    queryFn: () => api.get<PageResponse<GatePass>>(gatePassPath(type), { size: 50, ...filters }),
  })

export const useGatePass = (id?: string) =>
  useQuery({
    queryKey: ['gate-pass', id] as const,
    queryFn: () => api.get<GatePass>(`/api/v1/gate-passes/${id}`),
    enabled: Boolean(id),
  })

export const useOrderGatePasses = (orderId?: string) =>
  useQuery({
    queryKey: ['gate-passes', 'order', orderId] as const,
    queryFn: () => api.get<GatePass[]>(`/api/v1/orders/${orderId}/gate-passes`),
    enabled: Boolean(orderId),
  })

export const useEquipmentItems = (category?: string) =>
  useQuery({
    queryKey: ['equipment-items', category ?? 'all'] as const,
    queryFn: () => api.get<EquipmentItem[]>('/api/v1/equipment/items', { category }),
  })

const useEquipmentItemMutation = <TVariables,>(fn: (variables: TVariables) => Promise<unknown>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['equipment-items'] }),
  })
}

export const useCreateEquipmentItem = () =>
  useEquipmentItemMutation((body: Record<string, unknown>) =>
    api.post<EquipmentItem>('/api/v1/equipment/items', body),
  )

export const useUpdateEquipmentItem = () =>
  useEquipmentItemMutation(({ id, body }: { id: string; body: Record<string, unknown> }) =>
    api.put<EquipmentItem>(`/api/v1/equipment/items/${id}`, body),
  )

/** Every gate pass mutation invalidates the same two families. */
const useGatePassMutation = <TVariables,>(fn: (variables: TVariables) => Promise<unknown>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gate-passes'] })
      queryClient.invalidateQueries({ queryKey: ['gate-pass'] })
    },
  })
}

/**
 * The lists of what goes out on a job, narrowed to the register being raised
 * in and the line of business it is for.
 */
export const useGatePassTemplates = (
  issueType: 'OUTDOOR_VESSEL' | 'HALL_ASSET',
  serviceType?: string,
) =>
  useQuery({
    queryKey: ['gate-pass-templates', issueType, serviceType ?? 'any'] as const,
    queryFn: () =>
      api.get<GatePassTemplate[]>('/api/v1/gate-pass-templates', { issueType, serviceType }),
  })

const useTemplateMutation = <TVariables,>(fn: (variables: TVariables) => Promise<unknown>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gate-pass-templates'] }),
  })
}

/** What one dish travels in, is served with, and is cooked from. */
export const useDishRequirements = (itemId?: string) =>
  useQuery({
    queryKey: ['dish-requirements', itemId] as const,
    queryFn: () => api.get<DishRequirements>(`/api/v1/menu/items/${itemId}/requirements`),
    enabled: Boolean(itemId),
  })

export const useSaveDishRequirements = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ itemId, body }: { itemId: string; body: Record<string, unknown> }) =>
      api.put<DishRequirements>(`/api/v1/menu/items/${itemId}/requirements`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dish-requirements'] }),
  })
}

export const useCreateGatePassTemplate = () =>
  useTemplateMutation((body: Record<string, unknown>) =>
    api.post<GatePassTemplate>('/api/v1/gate-pass-templates', body),
  )

export const useUpdateGatePassTemplate = () =>
  useTemplateMutation(({ id, body }: { id: string; body: Record<string, unknown> }) =>
    api.put<GatePassTemplate>(`/api/v1/gate-pass-templates/${id}`, body),
  )

/** Retires it. Passes raised from it are untouched. */
export const useDeleteGatePassTemplate = () =>
  useTemplateMutation((id: string) => api.delete(`/api/v1/gate-pass-templates/${id}`))

export const useCreateGatePass = () =>
  useGatePassMutation(({ type, body }: { type: 'OUTDOOR_VESSEL' | 'HALL_ASSET'; body: unknown }) =>
    api.post<GatePass>(gatePassPath(type), body),
  )

export const useUpdateGatePass = () =>
  useGatePassMutation(({ id, body }: { id: string; body: unknown }) =>
    api.put<GatePass>(`/api/v1/gate-passes/${id}`, body),
  )

export const useDispatchGatePass = () =>
  useGatePassMutation(({ id, body }: { id: string; body?: unknown }) =>
    api.post<GatePass>(`/api/v1/gate-passes/${id}/dispatch`, body ?? {}),
  )

export const useReturnGatePass = () =>
  useGatePassMutation(({ id, body }: { id: string; body: unknown }) =>
    api.post<GatePass>(`/api/v1/gate-passes/${id}/return`, body),
  )

export const useWaiveGatePassRecovery = () =>
  useGatePassMutation(({ id, waived, reason }: { id: string; waived: boolean; reason?: string }) =>
    api.post<GatePass>(`/api/v1/gate-passes/${id}/waive-recovery`, { waived, reason }),
  )

export const useEbReadings = (filters: {
  hallId?: string
  status?: string
  orderId?: string
  page?: number
  size?: number
}) =>
  useQuery({
    queryKey: ['eb-readings', filters] as const,
    queryFn: () => api.get<PageResponse<EbReading>>('/api/v1/eb-readings', { size: 50, ...filters }),
  })

export const useAuditLogs = (filters: {
  action?: string
  /** Narrows the trail to one person, opened from their own row in Users. */
  performedBy?: string
  from?: string
  to?: string
  page?: number
  size?: number
}) =>
  useQuery({
    queryKey: ['audit-logs', filters] as const,
    queryFn: () =>
      api.get<PageResponse<AuditLogEntry>>('/api/v1/audit-logs', { size: 50, ...filters }),
  })

export const useRemoveAuditLog = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api.delete<void>(`/api/v1/audit-logs/${id}`, reason ? { reason } : undefined),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['audit-logs'] }),
  })
}

export const useOrderEbReadings = (orderId?: string) =>
  useQuery({
    queryKey: ['eb-readings', 'order', orderId] as const,
    queryFn: () => api.get<EbReading[]>(`/api/v1/orders/${orderId}/eb-readings`),
    enabled: Boolean(orderId),
  })

const useEbMutation = <TVariables,>(fn: (variables: TVariables) => Promise<unknown>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['eb-readings'] }),
  })
}

export const useOpenEbReading = () =>
  useEbMutation((body: unknown) => api.post<EbReading>('/api/v1/eb-readings', body))

export const useCloseEbReading = () =>
  useEbMutation(({ id, body }: { id: string; body: unknown }) =>
    api.post<EbReading>(`/api/v1/eb-readings/${id}/close`, body),
  )

export const useCorrectEbReading = () =>
  useEbMutation(({ id, body }: { id: string; body: unknown }) =>
    api.put<EbReading>(`/api/v1/eb-readings/${id}`, body),
  )

// ------------------------------------------------- catalogue authoring

/** Menu and package writes all invalidate the catalogue families. */
const useCatalogueMutation = <TVariables, TData = unknown>(
  fn: (variables: TVariables) => Promise<TData>,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu'] })
      // The prefix, so every narrowing of the list is refreshed and not
      // just the one somebody happens to be looking at.
      queryClient.invalidateQueries({ queryKey: ['packages'] })
      queryClient.invalidateQueries({ queryKey: ['package'] })
    },
  })
}

export const useCreateMenuCategory = () =>
  useCatalogueMutation((body: unknown) => api.post<MenuCategory>('/api/v1/menu/categories', body))

/** Creates a category, or corrects one that already exists. */
export const useSaveMenuCategory = () =>
  useCatalogueMutation(({ id, body }: { id?: string; body: unknown }) =>
    id
      ? api.put<MenuCategory>(`/api/v1/menu/categories/${id}`, body)
      : api.post<MenuCategory>('/api/v1/menu/categories', body),
  )

export const useSaveMenuItem = () =>
  useCatalogueMutation(({ id, body }: { id?: string; body: unknown }) =>
    id ? api.put<MenuItem>(`/api/v1/menu/items/${id}`, body) : api.post<MenuItem>('/api/v1/menu/items', body),
  )

/**
 * Withdraws a dish, or removes it outright when nothing has been sold under it.
 * The answer says which of the two happened.
 */
export const useDeleteMenuItem = () =>
  useCatalogueMutation((id: string) =>
    api.delete<{ removed: boolean }>(`/api/v1/menu/items/${id}`),
  )

/**
 * Withdraws a category, or removes it outright when no dish sits in it.
 * The answer says which of the two happened.
 */
export const useDeleteMenuCategory = () =>
  useCatalogueMutation((id: string) =>
    api.delete<{ removed: boolean }>(`/api/v1/menu/categories/${id}`),
  )

/**
 * Withdraws a package, or removes it outright when nothing was quoted from it.
 * The answer says which of the two happened.
 */
export const useDeletePackage = () =>
  useCatalogueMutation((id: string) =>
    api.delete<{ removed: boolean }>(`/api/v1/packages/${id}`),
  )

/** Brings a spreadsheet of dishes in; an existing item code updates that dish. */
export const useImportMenuItems = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData()
      body.append('file', file)
      return uploadForm<MenuImportResult>('/api/v1/menu/items/import', body)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menu'] })
      queryClient.invalidateQueries({ queryKey: keys.menuCategories })
    },
  })
}

export const usePackageVersions = (packageId?: string) =>
  useQuery({
    queryKey: ['package', packageId, 'versions'] as const,
    queryFn: () => api.get<PackageVersion[]>(`/api/v1/packages/${packageId}/versions`),
    enabled: Boolean(packageId),
  })

/** Rules and choosable items behind the quotation builder. */
export const useSelectionRules = (packageId?: string, versionId?: string) =>
  useQuery({
    queryKey: ['package', packageId, 'selection-rules', versionId ?? 'current'] as const,
    queryFn: () =>
      api.get<PackageItem[]>(`/api/v1/packages/${packageId}/selection-rules`, { versionId }),
    enabled: Boolean(packageId),
  })

export const useCreatePackage = () =>
  useCatalogueMutation((body: unknown) => api.post<CateringPackage>('/api/v1/packages', body))

export const useSavePackageVersion = () =>
  useCatalogueMutation(({ packageId, versionId, body }: { packageId?: string; versionId?: string; body: unknown }) =>
    versionId
      ? api.put<PackageVersion>(`/api/v1/packages/versions/${versionId}`, body)
      : api.post<PackageVersion>(`/api/v1/packages/${packageId}/versions`, body),
  )

/** Corrects a package's header. Its prices and items live on the version. */
export const useEditPackage = () =>
  useCatalogueMutation(({ id, body }: { id: string; body: unknown }) =>
    api.put<CateringPackage>(`/api/v1/packages/${id}`, body),
  )

/**
 * The draft an edit should be written to: the current version when it is still
 * a draft, otherwise a new one opened from the published one.
 */
export const useEditableVersion = (packageId?: string) =>
  useQuery({
    queryKey: ['package-editable-version', packageId ?? ''] as const,
    queryFn: () => api.post<PackageVersion>(`/api/v1/packages/${packageId}/editable-version`, {}),
    enabled: Boolean(packageId),
    // Opening this creates a draft, so it must not be refetched behind the form.
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })

export const usePublishPackageVersion = () =>
  useCatalogueMutation((versionId: string) =>
    api.post<PackageVersion>(`/api/v1/packages/versions/${versionId}/publish`, {}),
  )

// ---------------------------------------------------------- invoicing

const useInvoiceMutation = <TVariables,>(fn: (variables: TVariables) => Promise<unknown>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
  })
}

/** What this order is about to be billed, and what has not been captured. */
export const usePreBilling = (orderId?: string, enabled = true) =>
  useQuery({
    queryKey: ['pre-billing', orderId] as const,
    queryFn: () => api.get<PreBillingCheck>(`/api/v1/orders/${orderId}/pre-billing`),
    enabled: Boolean(orderId) && enabled,
  })

/**
 * The order confirmation, the standing proforma and the standing tax
 * invoice compared line by line, against today's catalogue rate where a
 * line can be matched to a package or a menu item.
 */
export const useOrderDocumentAudit = (orderId?: string) =>
  useQuery({
    queryKey: ['document-audit', orderId] as const,
    queryFn: () => api.get<OrderDocumentAudit>(`/api/v1/orders/${orderId}/document-audit`),
    enabled: Boolean(orderId),
  })

/**
 * What amenities add to this order's total right now, past the order's own
 * stored figure - the hall's estimate until a meter has been read, the
 * metered amount once one has. Zero once a proforma or tax invoice has
 * already carried it into the order's own total.
 */
export const useOrderAmenitiesPending = (orderId?: string) =>
  useQuery({
    queryKey: ['amenities-pending', orderId] as const,
    queryFn: () => api.get<number>(`/api/v1/orders/${orderId}/amenities-pending`),
    enabled: Boolean(orderId),
  })

// ------------------------------------------------------ delivery splits

/** How each sitting on an order is split into parcels, keyed by sitting. */
export const useOrderSplits = (orderId?: string) =>
  useQuery({
    queryKey: ['order-splits', orderId] as const,
    queryFn: () => api.get<Record<string, DeliverySplit[]>>(`/api/v1/orders/${orderId}/splits`),
    enabled: Boolean(orderId),
  })

const useSplitMutation = <TVariables extends { orderId: string }>(
  fn: (variables: TVariables) => Promise<unknown>,
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['order-splits', variables.orderId] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
  })
}

export const useSaveOrderSplits = () =>
  useSplitMutation(({ eventId, splits }: { orderId: string; eventId: string; splits: unknown[] }) =>
    api.put(`/api/v1/order-events/${eventId}/splits`, { splits }),
  )

/**
 * Puts one drop right where the whole set can no longer be rewritten.
 *
 * <p>Once anything on the sitting has gone out, the set is fixed - the head
 * counts have to keep adding up. The drops still standing can still be
 * corrected, one at a time.</p>
 */
export const useUpdateOrderSplit = () =>
  useSplitMutation(({ splitId, body }:
    { orderId: string; splitId: string; body: Record<string, unknown> }) =>
    api.put(`/api/v1/order-splits/${splitId}`, body),
  )

export const useSetSplitStatus = () =>
  useSplitMutation(({ splitId, status }: { orderId: string; splitId: string; status: string }) =>
    api.post(`/api/v1/order-splits/${splitId}/status`, {}, { params: { status } }),
  )

export const useOrderInvoices = (orderId?: string) =>
  useQuery({
    queryKey: ['invoices', 'order', orderId] as const,
    queryFn: () => api.get<Invoice[]>(`/api/v1/orders/${orderId}/invoices`),
    enabled: Boolean(orderId),
  })

export const useCreateInvoice = () =>
  useInvoiceMutation((body: unknown) => api.post<Invoice>('/api/v1/invoices', body))

/** Lets through, or turns down, a discount parked on an invoice beyond the preparer's own authority. */
export const useDecideInvoiceDiscountApproval = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, approved, remarks }: { id: string; approved: boolean; remarks?: string }) =>
      api.post<Invoice>(`/api/v1/invoices/${id}/discount-approval`, { approved, remarks }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: keys.approvals })
    },
  })
}

/** Proforma sent ahead of the tax invoice, carrying the same figures. */
export const useCreateProforma = () =>
  useInvoiceMutation((body: unknown) => api.post<Invoice>('/api/v1/invoices/proforma', body))

/** Final tax invoice built from the order, including approved changes. */
export const useCreateFinalInvoice = () =>
  useInvoiceMutation((body: unknown) => api.post<Invoice>('/api/v1/invoices/final', body))

/**
 * Corrects a bill. A draft is anybody's to put right who may edit billing; one
 * already issued needs the authority to cancel one, because it is the same act.
 */
export const useUpdateInvoice = () =>
  useInvoiceMutation(({ id, body }: { id: string; body: unknown }) =>
    api.put<Invoice>(`/api/v1/invoices/${id}`, body),
  )

export const useIssueInvoice = () =>
  useInvoiceMutation((id: string) => api.post<Invoice>(`/api/v1/invoices/${id}/issue`, {}))

export const useCancelInvoice = () =>
  useInvoiceMutation(({ id, reason }: { id: string; reason: string }) =>
    api.post<Invoice>(`/api/v1/invoices/${id}/cancel`, { reason }),
  )

/** Retakes a bill's billed-to name, mobile, GSTIN and address off the customer record. */
export const useSyncInvoiceBillingName = () =>
  useInvoiceMutation((id: string) => api.post<Invoice>(`/api/v1/invoices/${id}/sync-billing-name`, {}))

// ------------------------------------------------------- e-invoicing

/** How this company files its e-invoices. */
/**
 * The outside accounts - email, WhatsApp, the SMS gateway.
 *
 * <p>The fields come from the server rather than being typed out again here, so
 * a provider that gains a setting gains a box on the screen without the two
 * drifting apart.</p>
 */
export const useIntegrations = () =>
  useQuery({
    queryKey: ['integrations'],
    queryFn: () => api.get<IntegrationAccount[]>('/api/v1/integrations'),
  })

export const useSaveIntegration = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      provider,
      enabled,
      values,
    }: {
      provider: string
      enabled: boolean
      values: Record<string, string>
    }) =>
      api.put<IntegrationAccount>(`/api/v1/integrations/${provider.toLowerCase()}`, {
        enabled,
        values,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['integrations'] }),
  })
}

/** Sends a real test message, because only a real one finds a refused relay. */
export const useTestIntegration = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ provider, target }: { provider: string; target: string }) =>
      api.post<IntegrationTestResult>(
        `/api/v1/integrations/${provider.toLowerCase()}/test`,
        { target },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['integrations'] }),
  })
}

export const useEInvoiceSettings = () =>
  useQuery({
    queryKey: ['e-invoice', 'settings'] as const,
    queryFn: () => api.get<EInvoiceSettings>('/api/v1/settings/e-invoice'),
  })

export const useSaveEInvoiceSettings = () =>
  useEInvoiceMutation((body: EInvoiceSettings) =>
    api.put<EInvoiceSettings>('/api/v1/settings/e-invoice', body),
  )

/** Where one invoice stands with the e-invoice portal. */
export const useEInvoice = (invoiceId?: string) =>
  useQuery({
    queryKey: ['e-invoice', invoiceId] as const,
    queryFn: () => api.get<EInvoiceStatus>(`/api/v1/invoices/${invoiceId}/e-invoice`),
    enabled: Boolean(invoiceId),
  })

/** Tax invoices billed to registered customers the portal has not signed. */
export const usePendingEInvoices = (enabled = true) =>
  useQuery({
    queryKey: ['e-invoice', 'pending'] as const,
    queryFn: () => api.get<EInvoiceStatus[]>('/api/v1/e-invoices/pending'),
    enabled,
  })

const useEInvoiceMutation = <TVariables,>(fn: (variables: TVariables) => Promise<unknown>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['e-invoice'] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
    },
  })
}

/** Writes down the IRN, acknowledgement and signed QR the portal returned. */
export const useRecordIrn = () =>
  useEInvoiceMutation(({ id, ...body }: { id: string } & Record<string, unknown>) =>
    api.post<EInvoiceStatus>(`/api/v1/invoices/${id}/e-invoice/irn`, body),
  )

/** Asks the e-invoice portal directly for the IRN, through the account set up under API configuration. */
export const useRequestIrn = () =>
  useEInvoiceMutation((id: string) =>
    api.post<EInvoiceStatus>(`/api/v1/invoices/${id}/e-invoice/request`),
  )

export const useRecordEInvoiceFailure = () =>
  useEInvoiceMutation(({ id, ...body }: { id: string } & Record<string, unknown>) =>
    api.post<EInvoiceStatus>(`/api/v1/invoices/${id}/e-invoice/failure`, body),
  )

export const useCancelIrn = () =>
  useEInvoiceMutation(({ id, ...body }: { id: string } & Record<string, unknown>) =>
    api.post<EInvoiceStatus>(`/api/v1/invoices/${id}/e-invoice/cancel`, body),
  )

// -------------------------------------------------------- scheduled reports

export const useScheduledReports = () =>
  useQuery({
    queryKey: ['scheduled-reports'],
    queryFn: () => api.get<ScheduledReportView[]>('/api/v1/scheduled-reports'),
  })

export const useSaveScheduledReport = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: unknown }) =>
      id
        ? api.put<ScheduledReportView>(`/api/v1/scheduled-reports/${id}`, body)
        : api.post<ScheduledReportView>('/api/v1/scheduled-reports', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] }),
  })
}

export const useRemoveScheduledReport = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/scheduled-reports/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] }),
  })
}
